// DateTimeField — canonical `datetime` field primitive. Promoted off the
// CHG-1167 _FieldStub onto the real FieldShell/useFieldIds/describedBy a11y
// pattern (DRIFT-910 slice 1, CHG-2457). Single `<input type="datetime-local">`.
// Storage: the native datetime-local string (`YYYY-MM-DDTHH:MM`); emits `null`
// when cleared. The ISO⇄datetime-local boundary conversion lives at the form
// layer (lib/datetime-local.ts) — this primitive is intentionally zone-naive,
// matching native input behaviour (BUG-1059 / DRIFT-208 caveat documented there).
//
// Contract: specs/_product/field-primitive-registry.md §1 ('datetime') + §2.
// Doctrine: feedback_field_primitive_components_canonical.md.
// a11y (§2): label associated to the input via FieldShell <label htmlFor=id>;
// validation error in a role="alert" live region; aria-invalid + aria-required
// on the input; aria-describedby links the input to its error + helper text nodes.

import type { FieldPrimitiveProps } from "./types";
import { FieldShell, fieldControlStyle, useFieldIds, describedBy } from "./FieldShell";

export interface DateTimeFieldProps extends FieldPrimitiveProps<string> {
  /** Optional min/max bounds forwarded to the native input. */
  min?: string;
  max?: string;
}

export function DateTimeField({
  name,
  value,
  onChange,
  label,
  error,
  disabled,
  required,
  helperText,
  min,
  max,
}: DateTimeFieldProps) {
  const ids = useFieldIds();
  return (
    <FieldShell
      htmlFor={ids.inputId}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      errorId={ids.errorId}
      helperId={ids.helperId}
    >
      {/* composition-lint-exempt: canonical v2 `datetime` primitive — this <input> IS the dispatch target (parallel to v1 components/forms/); rule 15's path-exemption omits the v2 components/fields/ canonical-owner dir (gate-aim gap, flagged for cross-team re-aim) (DRIFT-910) */}
      <input
        id={ids.inputId}
        name={name}
        type="datetime-local"
        value={value ?? ""}
        min={min}
        max={max}
        disabled={disabled}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(ids, error, helperText)}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        style={fieldControlStyle(Boolean(error))}
      />
    </FieldShell>
  );
}
